import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';

/* === Helpers === */
function sortVariablePart(str) {
  return str.replace(/[a-z]+/gi, m => m.split('').sort().join(''));
}
function compareTermsCanonical(a, b) {
  const va = a.match(/[a-z]+/i)?.[0] || '';
  const vb = b.match(/[a-z]+/i)?.[0] || '';
  if (va.length !== vb.length) return vb.length - va.length;
  const cmp = va.localeCompare(vb);
  if (cmp !== 0) return cmp;
  return a.localeCompare(b);
}
function formatCoeffVar(coeff, vars) {
  if (!vars) return String(coeff);
  if (coeff === 1) return vars;
  if (coeff === -1) return `-${vars}`;
  return `${coeff}${vars}`;
}
function normalizeTerm(raw) {
  let s = raw.replace(/\s+/g, '').replace(/\*/g, '');
  let sign = 1;
  if (s.startsWith('-')) { sign = -1; s = s.slice(1); }
  else if (s.startsWith('+')) { s = s.slice(1); }
  const vars = s.match(/[a-z]+/i)?.[0] || '';
  const coeffStr = s.replace(/[a-z]+/gi, '');
  let coeff = coeffStr === '' ? 1 : Number(coeffStr);
  if (Number.isNaN(coeff)) coeff = 1;
  const v = sortVariablePart(vars);
  const c = sign * coeff;
  if (!v) return String(c);
  if (c === 1) return v;
  if (c === -1) return `-${v}`;
  return `${c}${v}`;
}
function normalizeExpression(expr) {
  if (!expr || typeof expr !== 'string') return '';
  const prepared = expr
    .replace(/−/g, '-')
    .replace(/\s+/g, '')
    .replace(/\*/g, '')
    .replace(/-/g, '+-')
    .toLowerCase();
  const terms = prepared.split('+').filter(Boolean).map(normalizeTerm);
  terms.sort(compareTermsCanonical);
  return terms
    .map((t, i) => (i === 0 ? t : (t.startsWith('-') ? t : `+${t}`)))
    .join('')
    .replace(/\s+/g, '');
}

/* === GENERATE TASK === */
export function generateTask() {
  const variables = ['x', 'y', 'z'];
  let var1 = variables[Math.floor(Math.random() * variables.length)];
  let var2 = var1;
  while (var2 === var1) var2 = variables[Math.floor(Math.random() * variables.length)];

  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const c = Math.floor(Math.random() * 9) + 1;

  const innerTerm = b === 1 ? var2 : `${b}${var2}`;
  const inner = Math.random() < 0.5 ? `${innerTerm} + ${c}` : `${c} + ${innerTerm}`;

  const expression = `${a === 1 ? '' : a}${var1}(${inner})`;

  const ab = a * b;
  const ac = a * c;

  const firstTerm = formatCoeffVar(ab, sortVariablePart(var1 + var2));
  const secondTerm = formatCoeffVar(ac, var1);

  const terms = [firstTerm, secondTerm].sort(compareTermsCanonical);
  const expected = terms.join(' + ').replace('+ -', '- ');

  return {
    type: 'SimplifyParenthesesTask',
    data: { a, b, c, var1, var2, expression, expected }
  };
}

/* === RENDER TASK === */
export function renderTask({ data }) {
  return (
    <p>
      Uprość wyrażenie:{' '}
      <span className='task-math-inline'>
        <InlineMath math={data.expression} />
      </span>
    </p>
  );
}

/* === INPUT === */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder='np. 16xy + 20x'
    />
  );
}

/* === CHECK ANSWER === */
export function checkAnswer({ data }, userAnswer) {
  const left = normalizeExpression(userAnswer);
  const right = normalizeExpression(data.expected);
  return { isCorrect: left === right, expected: data.expected };
}

/* === ANSWER === */
export function renderAnswer({ data }) {
  return (
    <span>
      <InlineMath math={data.expected} />
    </span>
  );
}

/* === EXPLANATION === */
export function renderExplanation({ data }) {
  const { a, b, c, var1, var2, expected } = data;

  const formula = String.raw`a\cdot(b+c)=ab+ac`;

  const aStr = a === 1 ? '' : String(a);
  const bStr = b === 1 ? '' : String(b);

  // Desktop — jedna linia
  const step2Single = String.raw`
${aStr}${var1}\,(${bStr}${var2}+${c}) \;=\; ${aStr}${var1}\cdot ${bStr}${var2} \;+\; ${aStr}${var1}\cdot ${c} \;=\; ${expected}
`;

  // Mobile — wszystko WYŚRODKOWANE (array{c}) + "=" na końcu linii 2
  const step2Wrapped = String.raw`
\begin{array}{c}
${aStr}${var1}\,(${bStr}${var2}+${c})\; = \\\\
=\; ${aStr}${var1}\cdot ${bStr}${var2} \;+\; ${aStr}${var1}\cdot ${c} \; = \\\\
=\; ${expected}
\end{array}
`;

  return (
    <div className='exp-wrap' style={{ lineHeight: 1.7 }}>
      <style>{`
        .exp-wrap p { text-align: left; }
        /* Wycentruj wszystkie bloki KaTeX (również array/aligned) */
        .exp-wrap .katex-display { text-align: center !important; }
        .exp-wide { display: block; }
        .exp-narrow { display: none; }
        @media (max-width: 880px) {
          .exp-wide { display: none; }
          .exp-narrow { display: block; }
        }
      `}</style>

      <p><strong>Krok 1:</strong> Użyj wzoru mnożenia jednomianu przez sumę:</p>
      <BlockMath math={formula} />

      <p><strong>Krok 2:</strong> Rozwiń nawiasy i zapisz w uproszczonej postaci:</p>

      {/* Desktop */}
      <div className='exp-wide'>
        <BlockMath math={step2Single} />
      </div>

      {/* Mobile — każdy wiersz wyśrodkowany identycznie */}
      <div className='exp-narrow'>
        <BlockMath math={step2Wrapped} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'string' ? userAnswer : userAnswer?.raw ?? '';
  if (!raw) return <p><i>(brak odpowiedzi)</i></p>;
  return (
    <p>
      <InlineMath math={raw} />
    </p>
  );
}
